// İSG Portal — Yönetim (Admin) API çağrıları + tipleri.
//
// KAYNAK: api-contract.md §9 (Yönetim — Yetki + Firma). Backend kanonu; burada
// **varsayım YOK** — endpoint yolları, istek/yanıt alanları ve kimlik kuralları
// (Create → `value.uuid`; liste → `value` + `total`/`filteredTotal`) doğrudan
// sözleşmeden ve backend DTO'larından (Isgportal.Identity/Authorization/Admin,
// Isgportal.Api/Admin) türetilmiştir. Serileştirme camelCase'dir (§1).
//
// İki uç grubu: (A) yetki yönetimi (rol CRUD + modül kataloğu + PermissionMatrix)
// ve (B) firma yönetimi (cross-tenant Company → Branch → Site). Tümü sistem-admin
// yetkili; grant'ı olmayan principal → 403 (asıl yetki backend'dedir).

import { api } from './client';
import type { Empty } from './envelope';

// ============================================================================
// Ortak liste parametreleri (api-contract §3 — page/pageSize/search/sort)
// ============================================================================

/** Sayfalı + aranabilir + sıralı liste istek parametreleri (api-contract §3). */
export interface AdminListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

/** `AdminListParams`'ı client `searchParams` şekline indirger (undefined atlanır). */
function listParams(params: AdminListParams): Record<string, string | number | undefined> {
  return {
    page: params.page,
    pageSize: params.pageSize,
    search: params.search && params.search.length > 0 ? params.search : undefined,
    sortBy: params.sortBy,
    sortDir: params.sortDir,
  };
}

// ============================================================================
// (A) Yetki yönetimi — Roller (§9.1)
// ============================================================================

/** Rol görünümü — api-contract §9.1 (`RoleDto`). */
export interface RoleDto {
  uuid: string;
  /** Kanonik küçük-harf kimlik (sunucuda addan türetilir; salt-okur). */
  value: string;
  name: string;
  description: string | null;
  /** Zorunlu sistem-admin rolü — kilitli (CRUD + matris düzenlenemez). */
  isSystemAdmin: boolean;
  /** Dolu ise rol o firmaya kapsamlıdır (tenant-scoped). */
  companyId: number | null;
  isActive: boolean;
}

/** Rol oluşturma isteği — api-contract §9.1 (`{ name, description?, companyId? }`). */
export interface RoleCreateRequest {
  name: string;
  description?: string | null;
  companyId?: number | null;
}

/** Rol güncelleme isteği — api-contract §9.1 (`{ name, description?, isActive }`). */
export interface RoleUpdateRequest {
  name: string;
  description?: string | null;
  isActive: boolean;
}

/** `GET /admin/roles` — sayfalı + aranabilir cross-tenant rol listesi. */
export function listRoles(params: AdminListParams = {}) {
  return api.list<RoleDto>('admin/roles', { searchParams: listParams(params) });
}

/** `GET /admin/roles/{uuid}` — tek rol (yok → `404 ROLE_NOT_FOUND`). */
export function getRole(uuid: string) {
  return api.get<RoleDto>(`admin/roles/${uuid}`);
}

/** `POST /admin/roles` — grant'sız rol oluşturur; yeni kimlik `value.uuid` (201). */
export function createRole(body: RoleCreateRequest) {
  return api.post<RoleDto>('admin/roles', { json: body });
}

/** `PUT /admin/roles/{uuid}` — sistem-admin → `409 SYSTEM_ADMIN_ROLE_LOCKED`. */
export function updateRole(uuid: string, body: RoleUpdateRequest) {
  return api.put<RoleDto>(`admin/roles/${uuid}`, { json: body });
}

/** `DELETE /admin/roles/{uuid}` — soft-delete (`isActive=false`); sistem-admin kilitli. */
export function deleteRole(uuid: string) {
  return api.delete<Empty>(`admin/roles/${uuid}`);
}

// ============================================================================
// (A) Yetki yönetimi — Modül kataloğu (§9.2)
// ============================================================================

/** Katalogdaki tek aksiyon — api-contract §9.2 (`ModuleActionDto`). */
export interface ModuleActionDto {
  uuid: string;
  value: string;
  name: string;
  /** Anonim geçer — grant gerektirmez (matriste bilgilendirici). */
  isPublic: boolean;
  /** Herhangi kimlik-doğrulanmış geçer — grant gerektirmez (bilgilendirici). */
  allAuthorized: boolean;
  isActive: boolean;
}

/** Modül kataloğu düğümü — api-contract §9.2 (`ModuleDto`). */
export interface ModuleDto {
  uuid: string;
  value: string;
  name: string;
  isActive: boolean;
  actions: ModuleActionDto[];
}

/** `GET /admin/modules` — tam modül/aksiyon ağacı (salt-okur; matris kataloğu). */
export function getModuleCatalog() {
  return api.get<ModuleDto[]>('admin/modules');
}

// ============================================================================
// (A) Yetki yönetimi — Yetki matrisi (§9.3)
// ============================================================================

/** Matriste tek aksiyon satırı + o roldeki grant durumu — api-contract §9.3 (`MatrixActionDto`). */
export interface MatrixActionDto {
  uuid: string;
  value: string;
  name: string;
  /** Bu rol bu aksiyonu çağırabilir mi (RoleModuleAction grant'ı var mı). */
  granted: boolean;
}

/** Matriste tek modül — nested aksiyonlar grant durumunu taşır (`MatrixModuleDto`). */
export interface MatrixModuleDto {
  uuid: string;
  value: string;
  name: string;
  actions: MatrixActionDto[];
}

/** Rolün yetki matrisi — api-contract §9.3 (`RolePermissionMatrixDto`). */
export interface RolePermissionMatrixDto {
  roleUuid: string;
  roleName: string;
  /** true ise matris kilitli (düzenleme reddedilir — kendini-kilitleme guard'ı). */
  isSystemAdmin: boolean;
  modules: MatrixModuleDto[];
}

/** Tek grant/revoke kararı — api-contract §9.3 (`{ actionUuid, granted }`). */
export interface PermissionMatrixEntry {
  actionUuid: string;
  granted: boolean;
}

/** Matris güncelleme isteği — api-contract §9.3 (`{ permissions: [...] }`). */
export interface PermissionMatrixUpdateRequest {
  permissions: PermissionMatrixEntry[];
}

/** `GET /admin/roles/{uuid}/permissions` — rolün grant matrisi (tüm katalog + granted). */
export function getRolePermissions(uuid: string) {
  return api.get<RolePermissionMatrixDto>(`admin/roles/${uuid}/permissions`);
}

/**
 * `PUT /admin/roles/{uuid}/permissions` — sunucu **farkı** uygular + yetki cache'ini
 * geçersizleştirir (yeni yetki anında etkin). Sistem-admin → `409
 * SYSTEM_ADMIN_ROLE_LOCKED`; tanınmayan aksiyon → `400 UNKNOWN_ACTION`.
 */
export function updateRolePermissions(uuid: string, body: PermissionMatrixUpdateRequest) {
  return api.put<Empty>(`admin/roles/${uuid}/permissions`, { json: body });
}

// ============================================================================
// (B) Firma yönetimi — Firmalar (§9.4, cross-tenant)
// ============================================================================

/** Firma (admin) görünümü — api-contract §9.4 (`CompanyAdminDto`). */
export interface CompanyAdminDto {
  uuid: string;
  name: string;
  legalName: string | null;
  taxNumber: string | null;
  /** UTC ISO 8601 (§4). */
  createdAt: string | null;
}

/** Firmaya bağlı ilk yönetici (CorporateAdmin) — api-contract §9.4 (`admin`). */
export interface CompanyAdminSeed {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

/**
 * Firma oluşturma isteği — api-contract §9.4. `admin` verilirse firmaya bağlı bir
 * **CorporateAdmin** (tenant-scoped; sistem-admin DEĞİL) kurulur. Parola politikası
 * → `400 PASSWORD_POLICY_ERROR`; e-posta kullanımda → `409 USER_ALREADY_EXISTS`.
 */
export interface CompanyCreateRequest {
  name: string;
  legalName?: string | null;
  taxNumber?: string | null;
  admin?: CompanyAdminSeed | null;
}

/** Firma güncelleme isteği — api-contract §9.4 (`{ name, legalName?, taxNumber? }`). */
export interface CompanyUpdateRequest {
  name: string;
  legalName?: string | null;
  taxNumber?: string | null;
}

/** `GET /admin/companies` — cross-tenant firma listesi (tenant scope uygulanmaz). */
export function listCompanies(params: AdminListParams = {}) {
  return api.list<CompanyAdminDto>('admin/companies', { searchParams: listParams(params) });
}

/** `GET /admin/companies/{uuid}` — tek firma (yok → `404 COMPANY_NOT_FOUND`). */
export function getCompany(uuid: string) {
  return api.get<CompanyAdminDto>(`admin/companies/${uuid}`);
}

/** `POST /admin/companies` — firma (+opsiyonel CorporateAdmin) oluşturur; `value.uuid`. */
export function createCompany(body: CompanyCreateRequest) {
  return api.post<CompanyAdminDto>('admin/companies', { json: body });
}

/** `PUT /admin/companies/{uuid}` — firma meta günceller (yok → `404 COMPANY_NOT_FOUND`). */
export function updateCompany(uuid: string, body: CompanyUpdateRequest) {
  return api.put<CompanyAdminDto>(`admin/companies/${uuid}`, { json: body });
}

// ============================================================================
// (B) Firma yönetimi — Şubeler (§9.5) + Sahalar (§9.6)
// ============================================================================

/** Şube (admin) görünümü — api-contract §9.5 (`BranchAdminDto`). */
export interface BranchAdminDto {
  uuid: string;
  companyId: number;
  name: string;
  code: string | null;
  createdAt: string | null;
}

/** Saha (admin) görünümü — api-contract §9.6 (`SiteAdminDto`). */
export interface SiteAdminDto {
  uuid: string;
  companyId: number;
  branchId: number;
  name: string;
  code: string | null;
  createdAt: string | null;
}

/** Şube/saha oluşturma/güncelleme isteği — api-contract §9.5/§9.6 (`{ name, code? }`). */
export interface BranchSiteWriteRequest {
  name: string;
  code?: string | null;
}

/** `GET /admin/companies/{uuid}/branches` — firmanın şubeleri (firma yok → 404). */
export function listBranches(companyUuid: string, params: AdminListParams = {}) {
  return api.list<BranchAdminDto>(`admin/companies/${companyUuid}/branches`, {
    searchParams: listParams(params),
  });
}

/** `POST /admin/companies/{uuid}/branches` — şube oluşturur; `value.uuid`. */
export function createBranch(companyUuid: string, body: BranchSiteWriteRequest) {
  return api.post<BranchAdminDto>(`admin/companies/${companyUuid}/branches`, { json: body });
}

/** `PUT /admin/companies/{uuid}/branches/{branchUuid}` — şube günceller. */
export function updateBranch(
  companyUuid: string,
  branchUuid: string,
  body: BranchSiteWriteRequest,
) {
  return api.put<BranchAdminDto>(`admin/companies/${companyUuid}/branches/${branchUuid}`, {
    json: body,
  });
}

/** `GET /admin/companies/{uuid}/branches/{branchUuid}/sites` — şubenin sahaları. */
export function listSites(companyUuid: string, branchUuid: string, params: AdminListParams = {}) {
  return api.list<SiteAdminDto>(`admin/companies/${companyUuid}/branches/${branchUuid}/sites`, {
    searchParams: listParams(params),
  });
}

/** `POST .../sites` — saha oluşturur; `value.uuid`. */
export function createSite(companyUuid: string, branchUuid: string, body: BranchSiteWriteRequest) {
  return api.post<SiteAdminDto>(`admin/companies/${companyUuid}/branches/${branchUuid}/sites`, {
    json: body,
  });
}

/** `PUT .../sites/{siteUuid}` — saha günceller. */
export function updateSite(
  companyUuid: string,
  branchUuid: string,
  siteUuid: string,
  body: BranchSiteWriteRequest,
) {
  return api.put<SiteAdminDto>(
    `admin/companies/${companyUuid}/branches/${branchUuid}/sites/${siteUuid}`,
    { json: body },
  );
}
